/**
 * 领养池（cfg_adopt_pool）的前端类型与默认值。
 *
 * **一个领养点一行**：收养馆 `shop` 与草丛 `grass` 各自一份完整配置（品质档权重、
 * 成员权重、衰减、本命、保底、公示文案），互不影响。2026-09-11 之前是「一行同时管
 * 两个点、再按主建筑星级分段」，分段已随 0131 迁移删除。
 *
 * 成员是**显式勾选**的：先在多选框里挑出这个点要爆哪些宠物，再给挑出来的配权重。
 * 权重填相对值，服务端归一——所以 5:3:2 和 50:30:20 是同一份配置。
 */

import { pickText } from "./locales.ts";

/**
 * 品质档位的**权重键**（档位号）。与服务端 game/pet-quality.ts 的 PET_QUALITY_CODES 同一份，由 adoptPool.test.ts 比对。
 * 2026-10-06 起权重 JSON 用档位号做键，不再用字母：R/SR/SSR 改名 N/R/SR 后同一个字母指另一档。
 */
export const QUALITY_CODES = ["1", "2", "3"] as const;
export type QualityCode = (typeof QUALITY_CODES)[number];
/**
 * 档位号 → 显示名。**只管展示**（表头、角标、公示）。与服务端 PET_QUALITY_LABEL 同一份，由 adoptPool.test.ts 比对。
 * 产品改名只改这里和服务端那张表。
 */
export const QUALITY_LABEL: Record<QualityCode, string> = { 1: "N", 2: "R", 3: "SR" };
/** 档位号（数字）→ 显示名；越界返回空串 */
export const qualityLabelOf = (level: number): string => QUALITY_LABEL[String(level) as QualityCode] ?? "";
/** 保底兑现的下限档（第 2 档）与最高档的显示名，公示文案用 */
const PITY = QUALITY_LABEL["2"], TOP = QUALITY_LABEL["3"];
/**
 * 领养点。与服务端 `src/game/adopt-pool-shape.ts` 的 ADOPT_POINTS 同一份。
 *
 * 管端与服务端不共享代码，所以只能各存一份 + 用例比对（adoptPool.test.ts 有一条）。
 * 分叉的后果不是「显示不一致」：前端放行的点，写接口当场拒。
 */
export const ADOPT_POINTS = ["shop", "grass"] as const;
export type AdoptPoint = (typeof ADOPT_POINTS)[number];
/** 单只 / 单档权重上限。与服务端 pet-draw.ts 的 MAX_PET_WEIGHT 同一份 */
export const MAX_WEIGHT = 100_000;
/** bps 制的取值范围（10000 = ×1.0） */
export const BPS_MIN = 1;
export const BPS_MAX = 100_000;
/**
 * 其余数值字段的取值范围与公示文案长度上限。**与 src/admin/adopt-pool.ts 的写接口逐条一致**
 * ——`NumInput` 只把输入解析成数字，`min`/`max` 是 DOM 属性，不替业务状态钳位，
 * 所以越界与小数会一路进 state，直到保存才被服务端拒（那时整批已经写了一半）。
 */
export const PITY_MAX = 10_000;
export const NOTICE_MAX = 2000;

/** 钳成服务端能收的整数。取整而不是拒绝：输入框里敲 `1.` 这类中间态很常见 */
export const clampInt = (v: unknown, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, Math.trunc(Number(v) || 0)));

const outOfRange = (p: AdoptPool): boolean =>
  [p.decayBps, p.oshiBoostBps, p.pitySr].some((v) => !Number.isSafeInteger(v))
  || p.decayBps < BPS_MIN || p.decayBps > BPS_MAX
  || p.oshiBoostBps < BPS_MIN || p.oshiBoostBps > BPS_MAX
  || p.pitySr < 0 || p.pitySr > PITY_MAX
  || p.members.some((m) => !Number.isSafeInteger(m.weight) || m.weight < 0 || m.weight > MAX_WEIGHT);

const noticeTooLong = (p: AdoptPool): boolean =>
  Object.values(p.noticeI18n ?? {}).some((t) => t.length > NOTICE_MAX);

export interface AdoptMember {
  petId: string;
  /** 相对权重。0 = 选中了但这一轮不爆（比留着不选更省事，切回来不用重新挑） */
  weight: number;
}

export interface AdoptPool {
  point: AdoptPoint;
  /** `random` = 两级抽（稀有度档 → 档内成员）。`specific` 由 C 片放开 */
  spawnMode: string;
  qualityWeights: Record<string, number>;
  members: AdoptMember[];
  decayBps: number;
  oshiBoostBps: number;
  oshiRequiresUnlock: boolean;
  /** 0 = 本点不保底。只有收养馆允许 > 0 */
  pitySr: number;
  /**
   * 满级种类的额外权重系数，bps。10000 = 中性（不额外压）。
   * 叠加在「已拥有衰减」之上，把满级后的替代金币压成低频事件。
   * ⚠️ 当前不生效：升星是 V2，玩家的宠物恒为 1★，触发不了满级。
   */
  maxedDecayBps: number;
  noticeI18n: Record<string, string> | null;
  /*
   * **没有 enabled**（0149 删的列）。领养点的开关在玩法格上；池子这层再来一个之后
   * 「格子开着 + 池停用」发布不出去，而「格子关着」时池子没人读——它没有一个
   * 有意义的可达状态，留着只是多一个能把运营锁住的开关。
   */
}

/**
 * 成员多选框与权重表的行源：本版本的角色名册。
 *
 * **由 App 里的 `pets` 快照块派生，不单独存一份**。写接口确实也回了一份名册，
 * 但那是 `cfg_pet` 在服务端那一刻的投影：单独存的话，历史版本对比、切换已缓存版本、
 * 以及同一草稿里刚改还没保存的角色，三条路都会让池和名册来自两个不同的版本。
 * 派生还顺带解决了「同批新增角色 + 把它加进池」在页面上看不见新角色的问题。
 */
export interface AdoptRosterPet {
  petId: string;
  /**
   * **多语言名，不是扁平 `name`。**
   *
   * 管端改名只写 `nameI18n`，扁平 `name` 要等 `toServerPet` 由 `en` 派生、再从服务端读回
   * 才更新（见 web/src/lib/pet.ts）。所以同一草稿里新建或改过名的角色，
   * 读扁平 `name` 拿到的是**旧名字**，新建的更是空串——而空串 `??` 兜不住，
   * 成员表会显示成一片空白，运营按着错的名字配权重，没有任何报错。
   * 显示口径与 PetPage 一致：`pickText(nameI18n, uiLang) || petId`。
   */
  nameI18n: Record<string, string>;
  quality: number;
  enabled: boolean;
}

/**
 * 新建某个点的默认值。
 *
 * **默认指定方式**（产品 2026-09-14 定）：挑几只、各给权重，归一后就是概率——
 * 这是运营最常用也最好懂的那种。随机方式要配品质档稀有度权重，属于进阶用法。
 * 存量行不动（迁移后一律 random），只有新建走这个默认。
 *
 * 品质档权重默认全 0——**不给「看起来配好了」的假象**：哪一档有候选、该给多少，
 * 只有策划知道，默认填一组数字会让人以为已经调过了。
 * 保底只有收养馆有（产品结论 #5③），草丛必须是 0，写接口也这么拒。
 */
export const newAdoptPool = (point: AdoptPoint): AdoptPool => ({
  point, spawnMode: "specific",
  qualityWeights: { 1: 0, 2: 0, 3: 0 },
  members: [], decayBps: 3000, oshiBoostBps: 15000, oshiRequiresUnlock: true,
  pitySr: point === "shop" ? 20 : 0,
  // 10000 = 中性，不额外压。真要压是升星上线后的调参，默认不预设倾向
  maxedDecayBps: 10_000,
  noticeI18n: null,
});

/** 这一只的权重；不在成员表里 = 0（members 为空是「全部启用角色权重 1」的特例，见 setMembers） */
export const weightOf = (pool: AdoptPool, petId: string): number =>
  pool.members.find((x) => x.petId === petId)?.weight ?? 0;

const byPetId = (a: AdoptMember, b: AdoptMember) => a.petId.localeCompare(b.petId);

/**
 * 换一批成员（多选框的 onChange）。已在表里的保留原权重，新选中的给 1。
 *
 * **新成员默认 1 而不是 0**：勾选这个动作的意思就是「这只要爆」，给 0 等于勾了
 * 却抽不到，运营得再逐个填一遍才看得见效果。要临时停爆某一只就把权重改成 0，
 * 不必取消勾选（取消了下次还得重新找）。
 *
 * ⚠️ 全取消 → `members: []`。在**指定方式**下这是非法状态，由 `poolIssues` 的
 * `adopt.err.specificEmpty` 当场报出来——因为契约里 `[]` 的含义是
 * 「全部启用角色、权重均为 1」，与「只爆这几只」正好相反，留着就是一个
 * 静默把整册全上线的开关。随机方式下 `members` 不被引擎读取，空着无所谓。
 */
export function setMembers(pool: AdoptPool, petIds: string[]): AdoptPool {
  const cur = new Map(pool.members.map((m) => [m.petId, m]));
  return {
    ...pool,
    members: [...new Set(petIds)].map((petId) => cur.get(petId) ?? { petId, weight: 1 }).sort(byPetId),
  };
}

/** 改一只的权重。只对已选中的成员有效——没选中的行不在表里，压根没有输入框 */
export function setWeight(pool: AdoptPool, petId: string, v: number): AdoptPool {
  if (!pool.members.some((m) => m.petId === petId)) return pool;
  return { ...pool, members: pool.members.map((m) => (m.petId === petId ? { ...m, weight: v } : m)) };
}

/**
 * 保存失败时该把人送去哪一页：**哪个领养点的那一行真的变了**。
 *
 * 按点逐个比两侧，不是只遍历当前数组——只遍历 `cur` 的话，整行被删掉时那个点
 * 压根不会被迭代到，落点会退成「剩下的那一行」：删了草丛却把人送去收养馆，
 * 而收养馆的配置好着呢，运营看不出问题在哪。
 *
 * 两个点都变了取第一个（改动多于一处时任选其一都对）；都没变（只可能是出现了
 * 不认识的 point）退回收养馆——**落点必须是存在的页**，`setPage` 到一个不存在的
 * id 会渲染成空白，比不跳更糟。
 */
export function adoptFailPoint(cur: AdoptPool[], base: AdoptPool[]): AdoptPoint {
  const at = (xs: AdoptPool[], pt: string) => xs.find((y) => y.point === pt);
  return ADOPT_POINTS.find((pt) => JSON.stringify(at(cur, pt)) !== JSON.stringify(at(base, pt))) ?? "shop";
}

/**
 * 指定方式下每只的**最终概率**（千分数，和恒 1000）。
 *
 * 指定方式不分稀有度档，所以权重直接全池归一就是玩家看到的概率——
 * 这正是产品说的「配置相应的概率，概率之和为 1」。
 * 零权重的成员不参与归一（它们是「选中了但这一轮不爆」）。
 */
export function specificShareTenths(members: AdoptMember[]): Map<string, number> {
  const live = members.filter((m) => m.weight > 0);
  const share = largestRemainderTenths(live.map((m) => m.weight));
  return new Map(live.map((m, i) => [m.petId, share[i]]));
}

/**
 * 最大余数法把一组权重分成千分数，和恒等于 1000。
 *
 * 各自四舍五入会加不到 100：24:20:14:6 → 37.5+31.3+21.9+9.4 = 100.1。
 * 公示上各档之和不等于 100 是硬伤，所以先给每项取整数份，余下的份按小数部分
 * 从大到小补。余数相同时按**原顺序**定序，保证同一份配置每次算出的结果逐字一致。
 *
 * 公示文案与管端的 % 列都走这一个函数——两份实现必然漂移，而漂移的那一份是公示。
 */
export function largestRemainderTenths(weights: number[]): number[] {
  const total = weights.reduce((a, b) => a + Math.max(0, b), 0);
  if (total <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (Math.max(0, w) / total) * 1000);
  const base = raw.map(Math.floor);
  let rest = 1000 - base.reduce((a, b) => a + b, 0);
  const order = raw.map((v, i) => [v - base[i], i] as const)
    .sort((x, y) => (y[0] - x[0]) || (x[1] - y[1]));
  for (const [, i] of order) { if (rest <= 0) break; base[i] += 1; rest -= 1; }
  return base;
}

/**
 * 按当前配置生成概率公示文案。
 *
 * 公示是**合规件**，不是说明文字：写出去的数必须和引擎真正用的那份一致。
 * 手写必然对不上——策划调完品质档权重回头改文案，漏一次就是一份错误公示，
 * 而这种错没有任何报错，只有被投诉时才发现。所以这里从同一份配置算出来。
 *
 * 覆盖 adopt.notice.hint 要求的四件事：
 *   ① 各档基准概率（按权重归一化；权重全 0 时无从谈起，返回 null）
 *   ② 已拥有会降低同品质内再次抽到的相对概率（decayBps）
 *   ③ 本命加成及其生效条件（oshiBoostBps + oshiRequiresUnlock）
 *   ④ 保底次数（pitySr > 0 时；草丛是 0，那一句写的是「不参与保底」）
 *
 * **基准概率**的口径来自决策清单 #6：公示固定的基准值、衰减用文字说明，
 * 不做逐玩家实时计算。所以这里算的是「没有任何衰减与加成时」的各档分布。
 *
 * 百分比留一位小数：权重 1:1:1:1 是 25.0%，而 14:20:24:6 这种会出现 .5。
 */
export function noticeDraft(pool: AdoptPool, lang: string, roster: AdoptRosterPet[] = []): string | null {
  /*
   * 指定方式：逐只列**最终概率**，而且**不写**衰减、本命、保底那三句——
   * 它们在这个方式下一律不生效（产品 2026-09-11 定），写进公示就是承诺一件
   * 不会发生的事。宠物名按语言取，取不到退回编号。
   */
  if (pool.spawnMode === "specific") {
    const share = specificShareTenths(pool.members);
    if (!share.size) return null;   // 一只都没挑（或全是 0 权重）→ 没有概率可公示
    const nameOf = (petId: string) => {
      const pet = roster.find((x) => x.petId === petId);
      return (pet && pickText(pet.nameI18n, lang)) || petId;
    };
    const q = (petId: string) => {
      const lv = roster.find((x) => x.petId === petId)?.quality ?? 0;
      return qualityLabelOf(lv);
    };
    const rows = pool.members
      .filter((m) => m.weight > 0)
      .map((m) => `${nameOf(m.petId)}${q(m.petId) ? `（${q(m.petId)}）` : ""} ${((share.get(m.petId) ?? 0) / 10).toFixed(1)}%`)
      .join(" / ");
    if (lang.startsWith("zh")) return `本领养点的爆出概率：${rows}。`;
    if (lang.startsWith("ja")) return `この排出地点の確率：${rows}。`;
    return `Drop rates at this spot: ${rows}.`;
  }

  const w = pool.qualityWeights;
  const share = largestRemainderTenths(QUALITY_CODES.map((c) => w[c] ?? 0));
  // 权重全 0 = 这个点还没配，没有概率可公示。返回 null 让调用方别写出一份「0%」的假公示
  if (!share.some((v) => v > 0)) return null;
  const rows = QUALITY_CODES.map((c, i) => `${QUALITY_LABEL[c]} ${(share[i] / 10).toFixed(1)}%`).join(" / ");
  const decayPct = (100 - pool.decayBps / 100).toFixed(0);
  const oshiPct = (pool.oshiBoostBps / 100 - 100).toFixed(0);

  if (lang.startsWith("zh")) {
    return [
      `基准概率：${rows}。`,
      `已拥有的种类在同一品质档内的相对权重降低 ${decayPct}%，不改变各品质档之间的概率。`,
      pool.oshiRequiresUnlock
        ? `已解锁 idol 的本命宠物在同档内权重提升 ${oshiPct}%（未解锁不生效）。`
        : `本命宠物在同档内权重提升 ${oshiPct}%。`,
      pool.pitySr > 0
        ? `连续 ${pool.pitySr} 次未出 ${PITY} 及以上时，下一次必出（${TOP} 同样算兑现）。草丛不计入此保底。`
        : pool.point === "grass"
          ? `草丛不参与 ${PITY} 保底，也不推进保底计数。`
          : `本领养点不参与 ${PITY} 保底，也不推进保底计数。`,
    ].join("\n");
  }
  if (lang.startsWith("ja")) {
    return [
      `基準確率：${rows}。`,
      `既に所持している種類は同じレアリティ内での相対的な重みが ${decayPct}% 下がります（レアリティ間の確率は変わりません）。`,
      pool.oshiRequiresUnlock
        ? `解放済みアイドルの「推しペット」は同レアリティ内で重みが ${oshiPct}% 上がります（未解放の場合は無効）。`
        : `「推しペット」は同レアリティ内で重みが ${oshiPct}% 上がります。`,
      pool.pitySr > 0
        ? `${PITY} 以上が ${pool.pitySr} 回連続で出なかった場合、次回は必ず排出されます（${TOP} も達成とみなします）。草むらはこの天井に含まれません。`
        : pool.point === "grass"
          ? `草むらは ${PITY} 天井の対象外で、カウントも進みません。`
          : `この排出地点は ${PITY} 天井の対象外で、カウントも進みません。`,
    ].join("\n");
  }
  return [
    `Base rates: ${rows}.`,
    `Species you already own have their relative weight within the same rarity reduced by ${decayPct}%; the odds between rarities are unchanged.`,
    pool.oshiRequiresUnlock
      ? `A favourite pet of an unlocked idol gets ${oshiPct}% more weight within its rarity (no effect while that idol is locked).`
      : `A favourite pet gets ${oshiPct}% more weight within its rarity.`,
    pool.pitySr > 0
      ? `After ${pool.pitySr} draws without ${PITY} or above, the next draw is guaranteed to be ${PITY} or above (${TOP} also counts). Grass draws do not count toward this.`
      : pool.point === "grass"
        ? `Grass is not covered by the ${PITY} guarantee and does not advance its counter.`
        : `This spot is not covered by the ${PITY} guarantee and does not advance its counter.`,
  ].join("\n");
}

/**
 * 公示文案里的基准概率与当前配置对不对得上。
 *
 * 只查最要命的那一项：**各档百分比**。改完权重忘了重新生成，公示就成了
 * 一份写着旧概率的合规文件——没有报错、没有红字，只有被投诉时才发现。
 * 文案是自由文本（运营可以自己润色），所以不比全文、只看那串数字还在不在。
 *
 * ⚠️ **不能用 `text.includes("R 25.0%")` 找 R 档**：`"SR 25.0%"`
 * 里也含有这个子串。各档百分比撞上相等（1:1:1 就是）时，删掉 R 那一档的公示
 * 照样判定为「不过期」，于是一份缺档的错误公示能一路发布出去
 * （服务端只校验 `en` 非空）。所以从文本里**解析出档位 → 百分比**再逐档比。
 */
export function noticeStale(pool: AdoptPool, roster: AdoptRosterPet[] = []): boolean {
  const text = pool.noticeI18n?.en?.trim();
  if (!text) return false;                        // 没填是「缺」不是「过期」，另有一条报它
  /*
   * 指定方式：每只 `weight > 0` 的成员，**名字与它的百分比都要出现在文本里**
   * （不要求相邻）。松到允许运营润色措辞，紧到能抓住「改了权重忘了重新生成」
   * 和「加/减了一只」这两件事。
   */
  if (pool.spawnMode === "specific") {
    const share = specificShareTenths(pool.members);
    if (!share.size) return false;                // 还没挑，谈不上对不对得上
    return pool.members.filter((m) => m.weight > 0).some((m) => {
      const pet = roster.find((x) => x.petId === m.petId);
      const name = (pet && pickText(pet.nameI18n, "en")) || m.petId;
      const pct = `${((share.get(m.petId) ?? 0) / 10).toFixed(1)}%`;
      return !text.includes(name) || !text.includes(pct);
    });
  }
  const share = largestRemainderTenths(QUALITY_CODES.map((c) => pool.qualityWeights[c] ?? 0));
  if (!share.some((v) => v > 0)) return false;    // 还没配权重，谈不上对不对得上
  // 左边界用 \b 不够：`\bR\b` 在 "SR" 的 R 前面不成立，但 "S" 与 "R" 之间也不是边界，
  // 所以显式要求显示名前面是行首或非字母。显示名从 QUALITY_LABEL 拼，改名不用动这里；
  // 改名前的旧公示（R/SR/SSR）里「R」「SR」指的是另一档，数字对不上就判过期——这正是要的
  const names = [...new Set(Object.values(QUALITY_LABEL))].sort((a, b) => b.length - a.length).join("|");
  const found = new Map<string, string>();
  for (const m of text.matchAll(new RegExp(`(?:^|[^A-Za-z])(${names}) (\\d+\\.\\d)%`, "g"))) found.set(m[1], m[2]);
  return QUALITY_CODES.some((c, i) => found.get(QUALITY_LABEL[c]) !== (share[i] / 10).toFixed(1));
}

/**
 * 界面上要拦的问题。**只列这一页自己看得见的**——
 * 跨表的（有没有 pet 掉落项、馆池够不够兑现保底）由发布闸负责，
 * 在这里重复实现会与服务端漂移，而且草稿阶段本来就允许不自洽。
 */
export function poolIssues(pool: AdoptPool, roster: AdoptRosterPet[]): string[] {
  const out: string[] = [];
  // 没有 enabled 了（0149 删的列），所以不再有「停用的行只体检一部分」这种分支：
  // 有行就是配了，全部判据一律体检
  if (outOfRange(pool)) out.push("adopt.err.numRange");
  if (noticeTooLong(pool)) out.push("adopt.err.noticeLong");
  // 草丛没有保底，填了数字写接口会拒（不生效的数字不进库）
  if (pool.point !== "shop" && pool.pitySr !== 0) out.push("adopt.err.pityPoint");
  // 成员里有停用角色：写接口会拒（「放进池里也抽不到」）。**只对指定方式有意义**——
  // 随机方式根本不读 members，那份名单留着只是为了切回去能直接用
  if (pool.spawnMode === "specific"
    && pool.members.some((m) => !roster.some((x) => x.petId === m.petId && x.enabled))) {
    out.push("adopt.err.memberOff");
  }
  if (!pool.noticeI18n?.en?.trim()) out.push("adopt.err.notice");

  /*
   * 判据按**爆出方式**分叉，因为两种方式看的是不同的字段：
   *   指定方式 —— 只看成员与权重（品质档权重在这个方式下不被引擎读取）
   *   随机方式 —— 只看品质档权重；候选来自全部启用角色，不看 members
   * 与发布闸 config-validate.ts 同一个口径，两边分叉就会一边放行一边拒。
   */
  if (pool.spawnMode === "specific") {
    // 空成员在契约里是「全员权重 1」，与「挑一部分」正好相反 → 指定方式下必须非法
    if (!pool.members.some((m) => m.weight > 0)) out.push("adopt.err.specificEmpty");
    return out;
  }

  const live = new Map(roster.filter((p) => p.enabled).map((p) => [p.petId, p]));
  // 随机方式的候选 = 全部启用角色，按品质自动归档
  const tiers = new Set<string>();
  for (const pet of live.values()) tiers.add(String(pet.quality));
  for (const code of QUALITY_CODES) {
    // 键尾带的是**显示名**：界面把它补在句子后面给运营看（AdoptPoolPanel 的 seg[3]）
    if ((pool.qualityWeights[code] ?? 0) > 0 && !tiers.has(code)) out.push(`adopt.err.tierNoCandidate.${QUALITY_LABEL[code]}`);
  }
  if (![...QUALITY_CODES].some((code) => (pool.qualityWeights[code] ?? 0) > 0 && tiers.has(code))) {
    out.push("adopt.err.allZero");
  }
  return out;
}
